import { useState } from "react";
import { SlidersHorizontal, AlertTriangle } from "lucide-react";
import { useLang } from "@/lib/adminLang";
import { kompakt, fortegn, nok, CAT_LABEL, MODE_LABEL, prodLabel, monthLabel } from "./budsjettUtils";
import AssumptionsDrawer from "./AssumptionsDrawer";

const Kpi = ({ label, verdi, sub, tone, testid }) => (
  <div data-testid={testid}>
    <p className={`bud-num text-[25px] tabular-nums leading-none ${tone || "text-neutral-900"}`}>{verdi}</p>
    <p className="mt-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-500">{label}</p>
    {sub && <p className="mt-1.5 text-[11.5px] text-neutral-500 leading-snug">{sub}</p>}
  </div>
);

const InvCol = ({ label, formula, big, sub }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-neutral-500">{label}</p>
    <p className="mt-1 text-[12px] text-neutral-500 tabular-nums">{formula}</p>
    <p className="mt-1.5 text-[18px] font-semibold tabular-nums leading-none">{big}</p>
    {sub && <p className="mt-1 text-[10.5px] text-neutral-400">{sub}</p>}
  </div>
);

export default function Overview({ data, scen, s, actions }) {
  const { t } = useLang();
  const c = data.computed;
  const inv = c.inventory;
  const be = s.breakEven;
  const budsjettKost = c.budgetCostTotal;
  const kostDiff = s.costs.total - budsjettKost;
  const scenDoc = data.scenarios.find((x) => x.key === scen) || {};
  const [assumptions, setAssumptions] = useState(false);
  const maksKat = Math.max(1, ...Object.values(s.costs.byCategory));
  const low = s.cash.lowest;

  return (
    <div data-testid="budget-overview" className="adm-rise">
      {/* Dominant profit statement — avbokset */}
      <div className="pt-1 pb-7" data-testid="ov-profit-block">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500"><span className="bud-mono font-normal mr-2 opacity-70">01</span>{t("Forecast profit")} · {scen}</p>
        <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-2">
          <p
            className={`adm-num-hero tabular-nums leading-none ${s.profit < 0 ? "text-red-600" : "text-emerald-700"}`}
            style={{ fontSize: "clamp(54px, 7vw, 92px)" }}
            data-testid="ov-profit-value"
          >
            {kompakt(s.profit)}
          </p>
          <p className={`text-[22px] font-semibold tabular-nums ${s.profit < 0 ? "text-red-600" : "text-neutral-400"}`}>{s.margin}% {t("margin")}</p>
        </div>
        <p className="mt-3 text-[13px] text-neutral-500 tabular-nums">
          {t("Revenue")} {kompakt(s.revenue.total)} − {t("costs")} {kompakt(s.costs.total)} · {t("actuals to date")} {kompakt(c.actualCostTotal)}
        </p>
      </div>

      {/* KPI-stripe med hairline-delere */}
      <div className="adm-strip grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
        <Kpi label={t("Forecast revenue")} verdi={kompakt(s.revenue.total)}
             sub={`${t("Physical")} ${kompakt(s.revenue.physical)} · ${t("partners")} ${kompakt(s.revenue.sponsors)}`} testid="ov-kpi-revenue" />
        <Kpi label={t("Forecast costs")} verdi={kompakt(s.costs.total)}
             sub={`${t("Budget")} ${kompakt(budsjettKost)} → ${fortegn(kostDiff)}`} tone={kostDiff > 0 ? "text-red-600" : undefined} testid="ov-kpi-costs" />
        <Kpi label={t("Physical sales")} verdi={`${s.physical.sold} / ${s.physical.sellable}`}
             sub={`${s.physical.sellThroughPct}% ${t("sell-through")} · ${s.physical.attendance} ${t("in the room")}`} testid="ov-kpi-physical" />
        <Kpi label={t("Digital passes")} verdi={s.digital.sold.toLocaleString("nb-NO")} sub={`${kompakt(s.revenue.digital)} ${t("net revenue")}`} testid="ov-kpi-digital" />
        <Kpi label={t("Sponsorship")} verdi={kompakt(c.sponsorSummary.signed)}
             sub={`${t("signed of")} ${kompakt(c.sponsorSummary.target || 0)} ${t("target")} · ${kompakt(c.sponsorSummary.weightedPipeline)} ${t("weighted pipeline")}`} testid="ov-kpi-sponsors" />
        <Kpi label={t("Lowest cash")} verdi={low ? kompakt(low.amount) : "—"}
             sub={low ? `${monthLabel(low.month)}${s.cash.fundingRequired ? ` · ${t("funding required")}` : ""}` : ""}
             tone={low && low.amount < 0 ? "text-red-600" : "text-emerald-700"} testid="ov-kpi-cash" />
      </div>

      {/* Key risks — computed by the engine (base) */}
      {(c.risks || []).length > 0 && (
        <div className="mt-12" data-testid="ov-risks">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-1"><span className="bud-mono font-normal mr-2 opacity-70">02</span>{t("Key risks")} · Base</p>
          <div>
            {c.risks.map((r, i) => (
              <p
                key={i}
                className={`flex items-start gap-3 text-[13px] leading-snug py-2.5 border-t border-[#F1EEE7] first:border-t-0 ${
                  r.tone === "red" ? "text-red-600" : r.tone === "amber" ? "text-amber-600" : "text-emerald-700"
                }`}
                data-testid={`ov-risk-${i}`}
              >
                <span className="adm-riskchip">{r.tone === "ok" ? "On track" : r.tone}</span>
                {r.text}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Assumptions */}
      <div className="adm-gruppe pt-5 mt-12" data-testid="ov-assumptions">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-neutral-500"><span className="bud-mono font-normal mr-2 opacity-70">03</span>{t("Assumptions")}</p>
          <button type="button" onClick={() => setAssumptions(true)} className="adm-btn-ghost adm-btn-sm" data-testid="ov-edit-assumptions-btn">
            <SlidersHorizontal size={12} /> {t("Edit assumptions")}
          </button>
        </div>
        {c.warnings.length > 0 && (
          <div className="mb-4 space-y-1" data-testid="ov-warnings">
            {c.warnings.map((w, i) => (
              <p key={i} className="flex items-center gap-2 text-[11.5px] text-amber-600"><AlertTriangle size={12} /> {w}</p>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <InvCol
            label={t("Physical inventory · Peer Gynt")}
            formula={`${inv.physical.venueCapacity} ${t("venue")} − ${inv.physical.operationalReserve} ${t("reserve")} − ${inv.physical.compsTotal} ${t("comps")}`}
            big={`${inv.physical.sellable} ${t("sellable")}`}
            sub={`${inv.physical.allocated} ${t("allocated to products")} · ${inv.physical.unallocated} ${t("unallocated")}`}
          />
          <InvCol
            label={t("Dinner inventory")}
            formula={`${inv.dinner.capacity} ${t("capacity")} − ${inv.dinner.includedExecutive} ${t("Executive")} − ${inv.dinner.partnerSpeakerAllocation} ${t("partner/speaker")}`}
            big={`${inv.dinner.sellable} ${t("sellable add-ons")}`}
            sub={`${inv.dinner.allocated} ${t("allocated")} · ${s.dinner.guests} ${t("guests in")} ${scen}`}
          />
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-neutral-500">{t("Scenario drivers")} · {scen}</p>
            <dl className="mt-1.5 space-y-1 text-[12px] tabular-nums">
              <div className="flex justify-between gap-3"><dt className="text-neutral-400">{t("Physical sell-through")}</dt><dd className="font-medium">{s.physical.sellThroughPct}%</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-neutral-400">{t("Digital passes sold")}</dt><dd className="font-medium">{s.digital.sold}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-neutral-400">{t("Sponsor revenue")}</dt><dd className="font-medium">{t(MODE_LABEL[scenDoc.sponsorMode] || scenDoc.sponsorMode)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-neutral-400">{t("Production level")}</dt><dd className="font-medium">{prodLabel(scenDoc.productionFactor)} ({scenDoc.productionFactor}×)</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-neutral-400">{t("Dinner guests")}</dt><dd className="font-medium">{s.dinner.guests}</dd></div>
            </dl>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-[#F1EEE7] flex flex-wrap gap-2">
          {inv.physical.comps.map((cp) => (
            <span key={cp.label} className="text-[10.5px] text-neutral-500 border border-[#E7E4DB] rounded-full px-2.5 py-0.5 tabular-nums">
              {cp.label} {cp.count}
            </span>
          ))}
          <span className="text-[10.5px] text-neutral-400 py-0.5">= {inv.physical.compsTotal} {t("comps")}</span>
        </div>
      </div>

      {/* Revenue engines */}
      <p className="mt-12 text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
        <span className="bud-mono font-normal mr-2 opacity-70">04</span>{t("Revenue engines")}
      </p>
      <div className="adm-strip grid-cols-2 md:grid-cols-4 mt-3" data-testid="ov-engines">
        {[
          [t("Physical summit"), s.revenue.physical, `${s.physical.sold} ${t("tickets")}`],
          [t("Digital"), s.revenue.digital, `${s.digital.sold} ${t("passes")}`],
          [t("Summit dinner"), s.revenue.dinner, `${s.dinner.addonsSold} ${t("add-ons")}`],
          [t("Partners"), s.revenue.sponsors, t(MODE_LABEL[scenDoc.sponsorMode] || "")],
        ].map(([navn, sum, sub]) => (
          <div key={navn}>
            <p className="bud-num text-[21px] tabular-nums leading-none">{kompakt(sum)}</p>
            <p className="mt-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-500">{navn}</p>
            <p className="mt-1.5 text-[11.5px] text-neutral-500">{sub}{s.revenue.total > 0 ? ` · ${Math.round((sum / s.revenue.total) * 100)}%` : ""}</p>
          </div>
        ))}
      </div>

      {/* Break-even + costs by category + variances */}
      <p className="mt-12 text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
        <span className="bud-mono font-normal mr-2 opacity-70">05</span>{t("Break-even & variances")}
      </p>
      <div className="adm-delt grid-cols-1 lg:grid-cols-3 mt-3">
        <div data-testid="ov-breakeven">
          <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-neutral-500 mb-3">{t("Break-even")} · {scen}</p>
          <p className="text-[26px] font-semibold tabular-nums leading-none">
            {be.physicalTicketsRequired == null ? "—" : be.physicalTicketsRequired.toLocaleString("nb-NO")}
            <span className="text-[12px] font-medium text-neutral-400 ml-2">{t("physical tickets required")}</span>
          </p>
          <p className="mt-1.5 text-[11px] text-neutral-400 tabular-nums">
            {t("of")} {s.physical.sellable} {t("sellable")} · {s.physical.sold} {t("sold in this forecast")}
          </p>
          <p className={`mt-4 text-[13px] font-semibold tabular-nums ${be.revenueGap > 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="ov-revenue-gap">
            {be.revenueGap > 0 ? `${kompakt(be.revenueGap)} ${t("revenue short of break-even")}` : t("Break-even reached at current forecast")}
          </p>
          <p className="mt-3 text-[10.5px] text-neutral-400 leading-relaxed">
            {t("Assumes")} {t(MODE_LABEL[scenDoc.sponsorMode] || "").toLowerCase()} {t("sponsors")} {kompakt(be.offsets.sponsors)} · {t("digital")} {kompakt(be.offsets.digital)} · {t("dinner")} {kompakt(be.offsets.dinner)}
            {be.offsets.other ? ` · ${t("other")} ${kompakt(be.offsets.other)}` : ""} · {t("contribution")} {nok(be.contribution)} {t("per ticket")}
          </p>
        </div>

        <div data-testid="ov-costcats">
          <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-neutral-500 mb-3">{t("Costs by category")}</p>
          {Object.entries(s.costs.byCategory)
            .sort((a, b) => b[1] - a[1])
            .map(([kat, sum]) => (
              <div key={kat} className="flex items-center gap-3 py-1">
                <span className="w-28 text-[11.5px] text-neutral-500 shrink-0 truncate">{t(CAT_LABEL[kat] || kat)}</span>
                <div className="flex-1 h-[7px] bg-[#F1EFE8] rounded-full overflow-hidden">
                  <div className="h-full bg-neutral-900 rounded-full transition-all" style={{ width: `${(sum / maksKat) * 100}%` }} />
                </div>
                <span className="w-14 text-right text-[11.5px] tabular-nums text-neutral-600">{kompakt(sum)}</span>
              </div>
            ))}
          <div className="flex items-center gap-3 py-1 mt-1 border-t border-[#F1EEE7] pt-2">
            <span className="w-28 text-[11.5px] text-neutral-400 shrink-0">{t("Contingency")}</span>
            <div className="flex-1" />
            <span className="w-14 text-right text-[11.5px] tabular-nums text-neutral-500">{kompakt(s.costs.contingency)}</span>
          </div>
        </div>

        <div data-testid="ov-variances">
          <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-neutral-500 mb-3">{t("Largest variances (Base)")}</p>
          {c.topVariances.length === 0 && <p className="text-xs text-neutral-400">{t("No variances yet.")}</p>}
          {c.topVariances.map((v) => (
            <div key={v.id} className="flex items-center gap-3 py-2 border-t border-[#F1EEE7] first:border-0">
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] font-medium text-neutral-800 truncate">{v.name}</p>
                <p className="text-[10.5px] text-neutral-400 tabular-nums">{t(CAT_LABEL[v.category] || v.category)} · {kompakt(v.budget)} → {kompakt(v.forecast)}</p>
              </div>
              <span className={`text-[12px] font-semibold tabular-nums ${v.diff > 0 ? "text-red-600" : "text-emerald-700"}`}>
                {fortegn(v.diff)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {assumptions && (
        <AssumptionsDrawer
          settings={data.settings}
          computed={c}
          onSave={async (invPayload) => {
            await actions.patchSettings({ inventory: invPayload });
            setAssumptions(false);
          }}
          onClose={() => setAssumptions(false)}
        />
      )}
    </div>
  );
}
